/*
 * Auto-adds the free 10ml vial once the cart holds enough 50ml bottles, and
 * takes it back out when the cart drops below the threshold.
 *
 * Shopify has no native way to put a product into a cart. "Buy X get Y" only
 * discounts a Y that is already there, so the gift has to be inserted by the
 * storefront. The vial is priced at 0, which also sidesteps a harder problem:
 * two automatic discounts of the same class never combine, so a Buy-X-get-Y
 * gift discount would have cancelled the bundle discount rather than stacking
 * with it.
 *
 * Stays completely inert until the vial product exists — a missing product
 * means one 404 and no further work — so this can ship before the product is
 * created in admin.
 */
(function () {
  'use strict';

  var CFG = window.PK_GIFT;
  if (!CFG || !CFG.eligible || !CFG.eligible.length) return;

  var busy = false;       // also stops our own cart writes re-triggering us
  var vial = null;        // null = not looked up, false = does not exist
  var vialIds = [];       // every variant of the vial, for spotting its cart line
  var pending = false;    // a cart write arrived while we were mid-reconcile

  function json(url, opts) {
    return fetch(url, opts).then(function (r) {
      if (!r.ok) throw new Error(r.status);
      return r.json();
    });
  }

  function getVial() {
    if (vial !== null) return Promise.resolve(vial);
    return json('/products/' + CFG.vial + '.js')
      .catch(function () { return false; })
      .then(function (p) {
        vial = p || false;
        if (vial) vialIds = vial.variants.map(function (v) { return v.id; });
        return vial;
      });
  }

  /* Match the gift to a scent they are actually buying, so the vial is a
     sample of something they chose rather than a random one. Falls back to the
     first available variant when nothing matches or that scent is out. */
  function pickVariant(product, handle) {
    var avail = product.variants.filter(function (v) { return v.available; });
    if (!avail.length) return null;
    if (handle) {
      var word = handle.replace(/^pikutin-/, '').toLowerCase();
      for (var i = 0; i < avail.length; i++) {
        if ((avail[i].title || '').toLowerCase().indexOf(word) > -1) return avail[i];
      }
    }
    return avail[0];
  }

  function post(path, body) {
    return json(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function reconcile() {
    /* A second cart write landing mid-reconcile used to be dropped on the
       floor, so two quick taps on Add to cart left the gift missing until the
       shopper touched the cart again. Remember that it happened and run once
       more when the current pass finishes. */
    if (busy) { pending = true; return Promise.resolve(false); }
    busy = true;
    pending = false;

    return json('/cart.js')
      .then(function (cart) {
        return getVial().then(function (product) {
          if (!product) return false;

          var bottles = 0, firstScent = null, vialLine = null;

          cart.items.forEach(function (item) {
            if (vialIds.indexOf(item.variant_id) > -1) { vialLine = item; return; }
            if (CFG.eligible.indexOf(item.handle) > -1) {
              bottles += item.quantity;
              if (!firstScent) firstScent = item.handle;
            }
          });

          var earned = bottles >= CFG.threshold;

          if (earned && !vialLine) {
            var v = pickVariant(product, firstScent);
            if (!v) return false;
            /* Shopify reports this to Meta as an AddToCart with no value.
               Tried /cart/update.js to avoid it (Sep 19 2026): Shopify tracks
               any new cart line, whichever endpoint added it, so the event
               fires either way. Accepted: it only inflates AddToCart counts;
               Purchase value is unaffected because the vial is priced 0. */
            return post('/cart/add.js', { items: [{ id: v.id, quantity: 1 }] })
              .then(function () { return true; });
          }
          if (!earned && vialLine) {
            return post('/cart/change.js', { id: vialLine.key, quantity: 0 })
              .then(function () { return true; });
          }
          /* Someone bumped the free line by hand. One per order. */
          if (earned && vialLine && vialLine.quantity > 1) {
            return post('/cart/change.js', { id: vialLine.key, quantity: 1 })
              .then(function () { return true; });
          }
          return false;
        });
      })
      .catch(function () { return false; })
      .then(function (changed) {
        busy = false;
        if (pending) setTimeout(run, 60);
        return changed;
      });
  }

  /* Re-render the cart UI. Writing to /cart/add.js updates the cart but leaves
     the drawer showing the markup Liquid produced at page load, which is why
     the gift used to appear only after a manual refresh. */
  function refresh() {
    if (typeof window.PkCartRefresh === 'function') window.PkCartRefresh();
  }

  /* The gift is earned by the bundle, not chosen by the shopper, so its line
     gets no quantity stepper and no remove button. The clamp in reconcile()
     already pulls a hand-edited quantity back to 1, but it is asynchronous:
     someone could tap + three times and hit Checkout before the correction
     landed, and the order went out with four free vials. Taking the control
     away removes the race instead of narrowing it. */
  function lockLines() {
    if (!vialIds.length) return;
    var rows = document.querySelectorAll('[data-key]');
    for (var i = 0; i < rows.length; i++) {
      var key = rows[i].getAttribute('data-key') || '';
      var id = parseInt(key.split(':')[0], 10);
      if (vialIds.indexOf(id) === -1) continue;
      rows[i].classList.add('pk-gift-line');
      var input = rows[i].querySelector('input[name="updates[]"]');
      if (input) { input.readOnly = true; input.tabIndex = -1; }
    }
  }

  /* Cart markup is replaced wholesale on every re-render, so the classes have
     to go back on afterwards. */
  var observer = new MutationObserver(function () { lockLines(); });

  function watch() {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* Last line of defence. Nothing should be able to reach checkout with the
     wrong gift quantity, including a stale cart from another tab. */
  function guardCheckout() {
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest
        ? e.target.closest('[name="checkout"]')
        : null;
      if (!btn || btn.getAttribute('data-pk-checked')) return;
      e.preventDefault();
      e.stopPropagation();
      reconcile().then(function () {
        btn.setAttribute('data-pk-checked', '1');
        btn.click();
      });
    }, true);
  }

  function run() {
    reconcile().then(function (changed) {
      if (changed) refresh();
      lockLines();
    });
  }

  /* Every theme path that touches the cart goes through one of these, so
     wrapping fetch catches the drawer, the cart page and the sticky bar
     without depending on any theme-specific event name. */
  var nativeFetch = window.fetch;
  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var result = nativeFetch.apply(this, arguments);
    if (!busy && /\/cart\/(add|change|update|clear)/.test(url)) {
      result.then(function () { setTimeout(run, 80); }).catch(function () {});
    }
    return result;
  };

  document.addEventListener('pk:revalidate', run);
  guardCheckout();
  watch();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
